import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Link,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  WrapItem,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FaSort, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { MdSearch, MdClear } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { ApplicationPathName, ApproveStatus, PurposeType, getPurposeTypeNameByCode, EquipmentUsageType, getEquipmentUsageTypeNameByCode, ReviewPhasePresets } from '../../contexts/constants';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { ReviewSearchItem, SearchReviewListInput, OrganizationWithHierarchyType } from '../../contexts/types';
import DebouncedInput from '../../components/DebouncedInput';
import TruncatedCell from '../../components/TruncatedCell';
import { useSelector } from '../../store/store';
import { getHeadquartersNameFromHierarchy, getDivisionNameFromHierarchy } from '../../utils/i18nDisplay';
import { openInvestmentScreeningDetailInNewTab } from '../../utils/utils';

/** 振り返り検索タブ内の列リサイズハンドル */
const ResizeHandle: React.FC<{ colIndex: number; setColWidths: React.Dispatch<React.SetStateAction<number[]>> }> = ({ colIndex, setColWidths }) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const th = (e.target as HTMLElement).closest('th');
    if (!th) return;
    const table = th.closest('table');
    if (!table) return;
    const ths = Array.from(table.querySelectorAll('thead th'));
    const startWidth = th.getBoundingClientRect().width;
    setColWidths(ths.map(t => t.getBoundingClientRect().width));
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      setColWidths(prev => {
        const next = [...prev];
        next[colIndex] = Math.max(50, startWidth + delta);
        return next;
      });
    };
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  return (
    <Box
      position="absolute"
      right="-2px"
      top={0}
      bottom={0}
      width="5px"
      cursor="col-resize"
      onMouseDown={handleMouseDown}
      _hover={{ bg: 'blue.300' }}
      zIndex={2}
    />
  );
};

type SortConfig = { key: string; ascending: boolean };

type ReviewStatusFilter = 'all' | 'inProgress' | 'notStarted';

/**
 * 振り返り検索タブ
 *  - 投資状況検索画面の2つ目のタブとして表示
 *  - 承認完了済み案件のうち、未完了の振り返り（代表1件）を検索
 *
 * 組織マスタ（accountingOrganizations）と適用年月（conditionFiscalPeriod）は
 * 親 InvestmentList.tsx で一元管理し props 経由で受け取る。両タブで共有することで
 * 重複取得を排除。
 */
interface ReviewSearchTabProps {
  accountingOrganizations: OrganizationWithHierarchyType[];
  conditionFiscalPeriod: string;
  onFiscalPeriodChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onResetFiscalPeriod: () => void;
}

const ReviewSearchTab: React.FC<ReviewSearchTabProps> = ({
  accountingOrganizations,
  conditionFiscalPeriod,
  onFiscalPeriodChange,
  onResetFiscalPeriod,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  // ---- 検索条件 state（申請検索タブと揃えた項目を先に配置）----
  const [conditionApplicationNumber, setConditionApplicationNumber] = useState<string>('');
  const [conditionProjectName, setConditionProjectName] = useState<string>('');
  const [conditionOpportunityName, setConditionOpportunityName] = useState<string>('');
  const [conditionMmiProductName, setConditionMmiProductName] = useState<string>('');
  const [conditionPurpose, setConditionPurpose] = useState<string>('');
  const [conditionRegistrantEmployeeId, setConditionRegistrantEmployeeId] = useState<string>('');
  const [conditionTargetStart, setConditionTargetStart] = useState<string>('');
  const [conditionTargetEnd, setConditionTargetEnd] = useState<string>('');
  const [reviewStatusFilter, setReviewStatusFilter] = useState<ReviewStatusFilter>('all');
  const [onlyMyAssignment, setOnlyMyAssignment] = useState<boolean>(false);
  const [onlyMyOriginatedProject, setOnlyMyOriginatedProject] = useState<boolean>(false);
  const [onlyNotStartedReview, setOnlyNotStartedReview] = useState<boolean>(false);

  // ---- 組織マスタ連動 Dropdown 用 state ----
  // accountingOrganizations と conditionFiscalPeriod は親 props から受け取る
  const [conditionHeadquarters, setConditionHeadquarters] = useState<string>('');
  const [conditionDivisionOrg, setConditionDivisionOrg] = useState<string>('');

  // ---- 列ヘッダ内フィルタ state ----
  const [filterProjectName, setFilterProjectName] = useState<string>('');
  const [filterPurpose, setFilterPurpose] = useState<string>('');
  const [filterEquipmentUsageType, setFilterEquipmentUsageType] = useState<string>('');
  const [filterHeadquarters, setFilterHeadquarters] = useState<string>('');
  const [filterDivision, setFilterDivision] = useState<string>('');
  const [filterRegistrant, setFilterRegistrant] = useState<string>('');
  const [filterReviewPhase, setFilterReviewPhase] = useState<string>('');
  const [filterStatusKind, setFilterStatusKind] = useState<string>('');
  const [filterApplicationNumber, setFilterApplicationNumber] = useState<string>('');
  const [filterOpportunityName, setFilterOpportunityName] = useState<string>('');
  const [filterMmiProductName, setFilterMmiProductName] = useState<string>('');

  // ---- 結果・制御 state ----
  const [results, setResults] = useState<ReviewSearchItem[]>([]);
  const [sort, setSort] = useState<SortConfig>({ key: 'targetReviewDate', ascending: true });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [colWidths, setColWidths] = useState<number[]>([]);

  // ---- ログインユーザー情報（userRole は onlyNotStartedReview 表示制御用）----
  const userRole = useSelector((state) => state.employee.userRole);

  // 適用年月が変わると組織マスタも親側で更新される → 振り返りタブの事業本部・事業部をリセット
  useEffect(() => {
    setConditionHeadquarters('');
    setConditionDivisionOrg('');
  }, [conditionFiscalPeriod]);

  // 事業本部変更時: 事業部をリセット
  const handleHeadquartersChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setConditionHeadquarters(e.target.value);
    setConditionDivisionOrg('');
  };

  // 事業部リスト（事業本部の配下の配下 = 事業部）
  const divisionOptions = useMemo(() => {
    if (!conditionHeadquarters) return [];
    const departments = accountingOrganizations.filter(o => o.parentOrganizationId === conditionHeadquarters);
    return departments.flatMap(dept =>
      accountingOrganizations.filter(o => o.parentOrganizationId === dept.organizationId)
    );
  }, [accountingOrganizations, conditionHeadquarters]);

  /**
   * 選択された組織の子孫IDを再帰的に収集して配列で返す
   * 事業本部のみ選択時: 事業本部 + 配下の全子孫ID
   * 事業部まで選択時: 事業部 + 配下の全子孫ID
   */
  const buildFilterOrganizationIds = useCallback((): string[] | undefined => {
    if (!conditionHeadquarters) return undefined;
    const rootOrgId = conditionDivisionOrg || conditionHeadquarters;
    const collectDescendantIds = (parentId: string): string[] => {
      const children = accountingOrganizations.filter(o => o.parentOrganizationId === parentId);
      return children.flatMap(c => [c.organizationId, ...collectDescendantIds(c.organizationId)]);
    };
    return [rootOrgId, ...collectDescendantIds(rootOrgId)];
  }, [conditionHeadquarters, conditionDivisionOrg, accountingOrganizations]);

  // ---- 検索API ----
  const handleSearch = async () => {
    const functionName = 'searchReviewList';
    setIsLoading(true);
    try {
      const input: SearchReviewListInput = {
        applicationNumber: conditionApplicationNumber || undefined,
        projectName: conditionProjectName || undefined,
        opportunityName: conditionOpportunityName || undefined,
        mmiProductName: conditionMmiProductName || undefined,
        purpose: conditionPurpose || undefined,
        filterOrganizationIds: buildFilterOrganizationIds(),
        registrantEmployeeId: conditionRegistrantEmployeeId || undefined,
        targetReviewDateStart: conditionTargetStart || undefined,
        targetReviewDateEnd: conditionTargetEnd || undefined,
        reviewStatusFilter,
        onlyMyAssignment: onlyMyAssignment || undefined,
        onlyMyOriginatedProject: onlyMyOriginatedProject || undefined,
        onlyNotStartedReview: onlyNotStartedReview || undefined,
      };

      const response = await WebAPI.request({
        query: `
          query ${functionName}($input: SearchReviewListInput) {
            ${functionName}(input: $input) {
              resultCount
              projects {
                projectId
                applicationNumber
                projectName
                opportunityName
                mmiProductName
                purpose
                equipmentUsageType
                divisionHierarchy
                divisionHierarchyEn
                registrantName
                targetReviewDate
                reviewPhase
                reviewStatus
                approvalStatus
                daysOverdue
              }
            }
          }`,
        variables: { input },
      });
      const data = response?.data?.[functionName];
      if (!data) {
        toast({
          description: t('features.Investment.List.reviewSearch.message.searchFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        setResults([]);
        return;
      }
      const items = (data.projects || []) as ReviewSearchItem[];
      setResults(items);
      if (items.length === 0) {
        toast({
          description: t('common.message.noDataFound'),
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error(`${functionName} error:`, err);
      toast({
        description: t('features.Investment.List.reviewSearch.message.searchFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ---- 検索条件リセット ----
  // 適用年月は親（InvestmentList）で管理されているため、リセットは親側のハンドラを呼んで委譲する
  const handleClearCondition = () => {
    setConditionApplicationNumber('');
    setConditionProjectName('');
    setConditionOpportunityName('');
    setConditionMmiProductName('');
    setConditionPurpose('');
    setConditionHeadquarters('');
    setConditionDivisionOrg('');
    setConditionRegistrantEmployeeId('');
    setConditionTargetStart('');
    setConditionTargetEnd('');
    setReviewStatusFilter('all');
    setOnlyMyAssignment(false);
    setOnlyMyOriginatedProject(false);
    setOnlyNotStartedReview(false);
    onResetFiscalPeriod();
  };


  // ---- 状態バッジ種別 ----
  const statusBadgeKind = useCallback((item: ReviewSearchItem): string => {
    if (item.reviewStatus === 'notStarted') {
      return item.daysOverdue > 0 ? 'overdue' : 'today';
    }
    if (item.reviewStatus === 'inProgress') {
      return item.approvalStatus === ApproveStatus.Pending.code ? 'inProgress' : 'approving';
    }
    return '';
  }, []);

  // ---- ソート値取得 ----
  const getSortValue = useCallback((item: ReviewSearchItem, key: string): string | number => {
    switch (key) {
      case 'projectName': return item.projectName || '';
      case 'purpose': return item.purpose || '';
      case 'equipmentUsageType': return getEquipmentUsageTypeNameByCode(item.equipmentUsageType || '') || '';
      case 'headquarters': return getHeadquartersNameFromHierarchy(item);
      case 'division': return getDivisionNameFromHierarchy(item);
      case 'registrantName': return item.registrantName || '';
      case 'targetReviewDate': return item.targetReviewDate || '';
      case 'reviewPhase': return item.reviewPhase || '';
      case 'status': return statusBadgeKind(item);
      case 'applicationNumber': return item.applicationNumber || '';
      case 'opportunityName': return item.opportunityName || '';
      case 'mmiProductName': return item.mmiProductName || '';
      default: return '';
    }
  }, [statusBadgeKind]);

  /**
   * 状態セルレンダリング（Home の「対応種別」列の Tag + Badge 構造に合わせる）
   *  - メインタグ: 進行状態（未着手 / 振り返り入力中 / 承認中）
   *  - サブバッジ: notStarted 時のみ 本日 / N日経過（Home の review タイプと同）
   */
  const renderStatusBadge = (item: ReviewSearchItem) => {
    // メインタグ（進行状態）
    let mainTag: React.ReactNode = null;
    if (item.reviewStatus === 'notStarted') {
      mainTag = (
        <Tag colorScheme="gray" size="sm" whiteSpace="nowrap" flexShrink={0}>
          {t('common.statusBadge.notStarted')}
        </Tag>
      );
    } else if (item.reviewStatus === 'inProgress') {
      if (item.approvalStatus === ApproveStatus.Pending.code) {
        mainTag = (
          <Tag colorScheme="blue" size="sm" whiteSpace="nowrap" flexShrink={0}>
            {t('common.statusBadge.reviewInProgress')}
          </Tag>
        );
      } else {
        mainTag = (
          <Tag colorScheme="red" size="sm" whiteSpace="nowrap" flexShrink={0}>
            {t('common.statusBadge.approving')}
          </Tag>
        );
      }
    }

    return (
      <Flex gap={1} align="center" wrap="wrap" whiteSpace="nowrap">
        {mainTag}
        {item.reviewStatus === 'notStarted' && item.daysOverdue === 0 && (
          <Badge colorScheme="orange" fontSize="xs" px={2} py={0.5} borderRadius="md" whiteSpace="nowrap" flexShrink={0}>
            {t('common.statusBadge.today')}
          </Badge>
        )}
        {item.reviewStatus === 'notStarted' && item.daysOverdue > 0 && (
          <Badge colorScheme="red" fontSize="xs" px={2} py={0.5} borderRadius="md" whiteSpace="nowrap" flexShrink={0}>
            {t('common.statusBadge.daysOverdue', { days: item.daysOverdue })}
          </Badge>
        )}
      </Flex>
    );
  };

  // ---- ソート+列フィルタ適用後の結果 ----
  const filteredSorted = useMemo(() => {
    const filtered = results.filter(item => {
      if (filterProjectName && !item.projectName.includes(filterProjectName)) return false;
      if (filterPurpose && item.purpose !== filterPurpose) return false;
      if (filterEquipmentUsageType && getEquipmentUsageTypeNameByCode(item.equipmentUsageType || '') !== filterEquipmentUsageType) return false;
      if (filterHeadquarters) {
        const hq = getHeadquartersNameFromHierarchy(item);
        if (!hq.includes(filterHeadquarters)) return false;
      }
      if (filterDivision) {
        const div = getDivisionNameFromHierarchy(item);
        if (!div.includes(filterDivision)) return false;
      }
      if (filterRegistrant && !(item.registrantName || '').includes(filterRegistrant)) return false;
      if (filterReviewPhase && !(item.reviewPhase || '').includes(filterReviewPhase)) return false;
      if (filterStatusKind && statusBadgeKind(item) !== filterStatusKind) return false;
      if (filterApplicationNumber && !(item.applicationNumber || '').includes(filterApplicationNumber)) return false;
      if (filterOpportunityName && !(item.opportunityName || '').includes(filterOpportunityName)) return false;
      if (filterMmiProductName && !(item.mmiProductName || '').includes(filterMmiProductName)) return false;
      return true;
    });
    const sorted = [...filtered].sort((a, b) => {
      const av = getSortValue(a, sort.key);
      const bv = getSortValue(b, sort.key);
      if (av < bv) return sort.ascending ? -1 : 1;
      if (av > bv) return sort.ascending ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [results, sort, filterProjectName, filterPurpose, filterEquipmentUsageType, filterHeadquarters, filterDivision, filterRegistrant, filterReviewPhase, filterStatusKind, filterApplicationNumber, filterOpportunityName, filterMmiProductName, getSortValue, statusBadgeKind]);

  // ---- ソートボタン ----
  const renderSortButton = (column: string) => (
    <IconButton
      h="16px"
      onClick={() => setSort(sort.key === column ? { key: column, ascending: !sort.ascending } : { key: column, ascending: true })}
      _hover={{ bg: 'gray.100', cursor: 'pointer' }}
      icon={sort.key === column ? (sort.ascending ? <FaSortAmountUp /> : <FaSortAmountDown />) : <FaSort />}
      aria-label={t('common.label.sort')}
    />
  );


  return (
    <Box h="100%" display="flex" flexDirection="column" gap={4}>
      {/* 検索条件エリア */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" p={4}>
        <Wrap spacing={4}>
          <WrapItem>
            <Box>
              <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={3}>
                {t('common.field.applicationNumber')}
              </FormLabel>
              <Input
                value={conditionApplicationNumber}
                onChange={(e) => setConditionApplicationNumber(e.target.value)}
                size="sm"
                placeholder={t('common.placeholder.partialMatch')}
              />
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={3}>
                {t('common.field.projectName')}
              </FormLabel>
              <Input
                value={conditionProjectName}
                onChange={(e) => setConditionProjectName(e.target.value)}
                size="sm"
                placeholder={t('common.placeholder.partialMatch')}
              />
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={3}>
                {t('common.field.opportunityName')}
              </FormLabel>
              <Input
                value={conditionOpportunityName}
                onChange={(e) => setConditionOpportunityName(e.target.value)}
                size="sm"
                placeholder={t('common.placeholder.partialMatch')}
              />
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={3}>
                {t('common.field.mmiProductName')}
              </FormLabel>
              <Input
                value={conditionMmiProductName}
                onChange={(e) => setConditionMmiProductName(e.target.value)}
                size="sm"
                placeholder={t('common.placeholder.partialMatch')}
              />
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={3}>
                {t('features.Investment.List.reviewSearch.filter.purpose')}
              </FormLabel>
              <Select size="sm" value={conditionPurpose} onChange={(e) => setConditionPurpose(e.target.value)}>
                <option value="">{t('common.label.all')}</option>
                {Object.values(PurposeType).map((p: any) => (
                  <option key={p.code} value={p.code}>{p.name}</option>
                ))}
              </Select>
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={3}>
                {t('features.Investment.List.reviewSearch.filter.targetReviewDate')}
              </FormLabel>
              <HStack spacing={2}>
                <Input
                  type="date"
                  size="sm"
                  value={conditionTargetStart}
                  onChange={(e) => setConditionTargetStart(e.target.value)}
                />
                <Text>〜</Text>
                <Input
                  type="date"
                  size="sm"
                  value={conditionTargetEnd}
                  onChange={(e) => setConditionTargetEnd(e.target.value)}
                />
              </HStack>
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={1}>
                {t('common.field.managementStructure')}
              </FormLabel>
              <Flex gap={3} align="center">
                <Box position="relative" mt={2}>
                  <Text position="absolute" top="-9px" left="8px" bg="white" px={1} fontSize="xs" color="gray.500" zIndex={1}>
                    {t('common.field.applicablePeriod')}
                  </Text>
                  <Input
                    type="month"
                    size="sm"
                    value={conditionFiscalPeriod}
                    onChange={onFiscalPeriodChange}
                  />
                </Box>
                <Box position="relative" mt={2}>
                  <Text position="absolute" top="-9px" left="8px" bg="white" px={1} fontSize="xs" color="gray.500" zIndex={1}>
                    {t('common.field.headquarters')}
                  </Text>
                  <Select
                    w="200px"
                    value={conditionHeadquarters}
                    onChange={handleHeadquartersChange}
                    size="sm"
                  >
                    <option value=""></option>
                    {accountingOrganizations
                      .filter(o => o.parentOrganizationId === 'organization00000')
                      .map(o => (
                        <option key={o.organizationId} value={o.organizationId}>
                          {o.organizationName}
                        </option>
                      ))}
                  </Select>
                </Box>
                <Box position="relative" mt={2}>
                  <Text position="absolute" top="-9px" left="8px" bg="white" px={1} fontSize="xs" color="gray.500" zIndex={1}>
                    {t('common.field.division')}
                  </Text>
                  <Select
                    w="200px"
                    value={conditionDivisionOrg}
                    onChange={(e) => setConditionDivisionOrg(e.target.value)}
                    size="sm"
                    disabled={!conditionHeadquarters}
                  >
                    <option value=""></option>
                    {divisionOptions.map(o => (
                      <option key={o.organizationId} value={o.organizationId}>
                        {o.organizationName}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Flex>
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={3}>
                {t('features.Investment.List.reviewSearch.filter.reviewStatus')}
              </FormLabel>
              <RadioGroup value={reviewStatusFilter} onChange={(v) => setReviewStatusFilter(v as ReviewStatusFilter)}>
                <Stack direction="row" spacing={3}>
                  <Radio value="all" size="sm">{t('features.Investment.List.reviewSearch.filter.statusOptionAll')}</Radio>
                  <Radio value="inProgress" size="sm">{t('features.Investment.List.reviewSearch.filter.statusOptionInProgress')}</Radio>
                  <Radio value="notStarted" size="sm">{t('features.Investment.List.reviewSearch.filter.statusOptionNotStarted')}</Radio>
                </Stack>
              </RadioGroup>
            </Box>
          </WrapItem>
        </Wrap>
        {/* チェックボックスとボタンを同じ行に配置（縦方向スペース節約） */}
        <Flex justify="space-between" align="center" mt={4} gap={4} flexWrap="wrap">
          <Wrap spacing={4}>
            <WrapItem>
              <Checkbox
                isChecked={onlyMyAssignment}
                onChange={(e) => setOnlyMyAssignment(e.target.checked)}
              >
                {t('features.Investment.List.reviewSearch.filter.onlyMyAssignment')}
              </Checkbox>
            </WrapItem>
            <WrapItem>
              <Checkbox
                isChecked={onlyMyOriginatedProject}
                onChange={(e) => setOnlyMyOriginatedProject(e.target.checked)}
              >
                {t('features.Investment.List.reviewSearch.filter.onlyMyOriginatedProject')}
              </Checkbox>
            </WrapItem>
            {userRole === '3' && (
              <WrapItem>
                <Checkbox
                  isChecked={onlyNotStartedReview}
                  onChange={(e) => setOnlyNotStartedReview(e.target.checked)}
                >
                  {t('features.Investment.List.reviewSearch.filter.onlyNotStartedReview')}
                </Checkbox>
              </WrapItem>
            )}
          </Wrap>
          <Flex gap={2}>
            <Button
              leftIcon={<MdSearch />}
              size="sm"
              colorScheme="blue"
              onClick={handleSearch}
              isLoading={isLoading}
            >
              {t('common.button.search')}
            </Button>
            <Button
              leftIcon={<MdClear />}
              size="sm"
              variant="outline"
              colorScheme="red"
              onClick={handleClearCondition}
            >
              {t('common.button.searchClear')}
            </Button>
          </Flex>
        </Flex>
      </Box>

      {/* 結果エリア（件数表示 + テーブル、既存申請検索タブと同構造） */}
      <Box flex={1} minH={0} bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden" display="flex" flexDirection="column">
        <Box px={4} py={2} borderBottom="1px solid" borderColor="gray.100" bg="white" position="relative" zIndex={2} flexShrink={0}>
          <Text fontSize="sm" color="gray.600">
            {t('common.message.searchResult')}: <Text as="span" fontWeight="bold" color="gray.800">{filteredSorted.length}</Text> {t('common.label.count')}
          </Text>
        </Box>
        <Box flex={1} minH={0} overflow="auto">
          <Table size="sm" variant="simple"
            sx={{ tableLayout: 'fixed' }}
            width={colWidths.length > 0 ? `${colWidths.reduce((a, b) => a + b, 0)}px` : "149%"}
          >
            <Thead position="sticky" top={0} zIndex={1}>
              <Tr>
                {/* 0: プロジェクト名（リンク） */}
                <Th position="relative" bg="gray.50" borderRight="1px solid" borderColor="gray.200" width={colWidths[0] ? `${colWidths[0]}px` : "22%"}>
                  <HStack spacing={1}>
                    <Text>{t('common.field.projectName')}</Text>
                    {renderSortButton('projectName')}
                  </HStack>
                  <DebouncedInput
                    value={filterProjectName}
                    onChange={setFilterProjectName}
                    size="xs"
                    mt={1}
                    placeholder={t('common.placeholder.partialMatch')}
                  />
                  <ResizeHandle colIndex={0} setColWidths={setColWidths} />
                </Th>
                {/* 1: 振り返り日 */}
                <Th position="relative" bg="gray.50" borderRight="1px solid" borderColor="gray.200" width={colWidths[1] ? `${colWidths[1]}px` : "10%"}>
                  <HStack spacing={1}>
                    <Text>{t('features.Investment.List.reviewSearch.column.targetReviewDate')}</Text>
                    {renderSortButton('targetReviewDate')}
                  </HStack>
                  <ResizeHandle colIndex={1} setColWidths={setColWidths} />
                </Th>
                {/* 2: フェーズ */}
                <Th position="relative" bg="gray.50" borderRight="1px solid" borderColor="gray.200" width={colWidths[2] ? `${colWidths[2]}px` : "10%"}>
                  <HStack spacing={1}>
                    <Text>{t('features.Investment.List.reviewSearch.column.reviewPhase')}</Text>
                    {renderSortButton('reviewPhase')}
                  </HStack>
                  <DebouncedInput
                    value={filterReviewPhase}
                    onChange={setFilterReviewPhase}
                    size="xs"
                    mt={1}
                    placeholder={t('common.placeholder.partialMatch')}
                  />
                  <ResizeHandle colIndex={2} setColWidths={setColWidths} />
                </Th>
                {/* 3: 状態 */}
                <Th position="relative" bg="gray.50" borderRight="1px solid" borderColor="gray.200" width={colWidths[3] ? `${colWidths[3]}px` : "12%"}>
                  <HStack spacing={1}>
                    <Text>{t('features.Investment.List.reviewSearch.column.status')}</Text>
                    {renderSortButton('status')}
                  </HStack>
                  <Select size="xs" mt={1} value={filterStatusKind} onChange={(e) => setFilterStatusKind(e.target.value)}>
                    <option value="">{t('common.label.all')}</option>
                    <option value="overdue">{t('common.label.overdue')}</option>
                    <option value="today">{t('common.statusBadge.today')}</option>
                    <option value="inProgress">{t('common.statusBadge.reviewInProgress')}</option>
                    <option value="approving">{t('common.statusBadge.approving')}</option>
                  </Select>
                  <ResizeHandle colIndex={3} setColWidths={setColWidths} />
                </Th>
                {/* 4: 目的 */}
                <Th position="relative" bg="gray.50" borderRight="1px solid" borderColor="gray.200" width={colWidths[4] ? `${colWidths[4]}px` : "10%"}>
                  <HStack spacing={1}>
                    <Text>{t('features.Investment.List.reviewSearch.column.purpose')}</Text>
                    {renderSortButton('purpose')}
                  </HStack>
                  <Select size="xs" mt={1} value={filterPurpose} onChange={(e) => setFilterPurpose(e.target.value)}>
                    <option value="">{t('common.label.all')}</option>
                    {Object.values(PurposeType).map((p: any) => (
                      <option key={p.code} value={p.code}>{p.name}</option>
                    ))}
                  </Select>
                  <ResizeHandle colIndex={4} setColWidths={setColWidths} />
                </Th>
                {/* 5: 設備用途区分 */}
                <Th position="relative" bg="gray.50" borderRight="1px solid" borderColor="gray.200" width={colWidths[5] ? `${colWidths[5]}px` : "9%"}>
                  <Box as="span" whiteSpace="nowrap">
                    <HStack spacing={1}>
                      <Text>{t('features.Investment.List.reviewSearch.column.equipmentUsageType')}</Text>
                      {renderSortButton('equipmentUsageType')}
                    </HStack>
                  </Box>
                  <Select size="xs" mt={1} value={filterEquipmentUsageType} onChange={(e) => setFilterEquipmentUsageType(e.target.value)}>
                    <option value="">{t('common.label.all')}</option>
                    {Object.values(EquipmentUsageType).map(({ code }) => <option key={code} value={getEquipmentUsageTypeNameByCode(code)}>{getEquipmentUsageTypeNameByCode(code)}</option>)}
                  </Select>
                  <ResizeHandle colIndex={5} setColWidths={setColWidths} />
                </Th>
                {/* 6: 事業本部 */}
                <Th position="relative" bg="gray.50" borderRight="1px solid" borderColor="gray.200" width={colWidths[6] ? `${colWidths[6]}px` : "12%"}>
                  <HStack spacing={1}>
                    <Text>{t('features.Investment.List.reviewSearch.column.businessHq')}</Text>
                    {renderSortButton('headquarters')}
                  </HStack>
                  <DebouncedInput
                    value={filterHeadquarters}
                    onChange={setFilterHeadquarters}
                    size="xs"
                    mt={1}
                    placeholder={t('common.placeholder.partialMatch')}
                  />
                  <ResizeHandle colIndex={6} setColWidths={setColWidths} />
                </Th>
                {/* 7: 事業部 */}
                <Th position="relative" bg="gray.50" borderRight="1px solid" borderColor="gray.200" width={colWidths[7] ? `${colWidths[7]}px` : "12%"}>
                  <HStack spacing={1}>
                    <Text>{t('features.Investment.List.reviewSearch.column.division')}</Text>
                    {renderSortButton('division')}
                  </HStack>
                  <DebouncedInput
                    value={filterDivision}
                    onChange={setFilterDivision}
                    size="xs"
                    mt={1}
                    placeholder={t('common.placeholder.partialMatch')}
                  />
                  <ResizeHandle colIndex={7} setColWidths={setColWidths} />
                </Th>
                {/* 8: 起案者 */}
                <Th position="relative" bg="gray.50" borderRight="1px solid" borderColor="gray.200" width={colWidths[8] ? `${colWidths[8]}px` : "11%"}>
                  <HStack spacing={1}>
                    <Text>{t('features.Investment.List.reviewSearch.column.registrant')}</Text>
                    {renderSortButton('registrantName')}
                  </HStack>
                  <DebouncedInput
                    value={filterRegistrant}
                    onChange={setFilterRegistrant}
                    size="xs"
                    mt={1}
                    placeholder={t('common.placeholder.partialMatch')}
                  />
                  <ResizeHandle colIndex={8} setColWidths={setColWidths} />
                </Th>
                {/* 9: 申請番号 */}
                <Th position="relative" bg="gray.50" borderRight="1px solid" borderColor="gray.200" width={colWidths[9] ? `${colWidths[9]}px` : "8%"}>
                  <HStack spacing={1}>
                    <Text>{t('common.field.applicationNumber')}</Text>
                    {renderSortButton('applicationNumber')}
                  </HStack>
                  <DebouncedInput
                    value={filterApplicationNumber}
                    onChange={setFilterApplicationNumber}
                    size="xs"
                    mt={1}
                    placeholder={t('common.placeholder.partialMatch')}
                  />
                  <ResizeHandle colIndex={9} setColWidths={setColWidths} />
                </Th>
                {/* 10: 案件名 */}
                <Th position="relative" bg="gray.50" borderRight="1px solid" borderColor="gray.200" width={colWidths[10] ? `${colWidths[10]}px` : "12%"}>
                  <HStack spacing={1}>
                    <Text>{t('common.field.opportunityName')}</Text>
                    {renderSortButton('opportunityName')}
                  </HStack>
                  <DebouncedInput
                    value={filterOpportunityName}
                    onChange={setFilterOpportunityName}
                    size="xs"
                    mt={1}
                    placeholder={t('common.placeholder.partialMatch')}
                  />
                  <ResizeHandle colIndex={10} setColWidths={setColWidths} />
                </Th>
                {/* 11: MMI製品名 */}
                <Th position="relative" bg="gray.50" width={colWidths[11] ? `${colWidths[11]}px` : "12%"}>
                  <HStack spacing={1}>
                    <Text>{t('common.field.mmiProductName')}</Text>
                    {renderSortButton('mmiProductName')}
                  </HStack>
                  <DebouncedInput
                    value={filterMmiProductName}
                    onChange={setFilterMmiProductName}
                    size="xs"
                    mt={1}
                    placeholder={t('common.placeholder.partialMatch')}
                  />
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredSorted.map((item, idx) => (
                <Tr key={`${item.projectId}#${item.targetReviewDate}#${idx}`}>
                  {/* 0: プロジェクト名（リンク） */}
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <Link
                      as={RouterLink}
                      to={ApplicationPathName.InvestmentScreeningDetail.Url}
                      state={{ projectId: item.projectId, reviewDate: item.targetReviewDate }}
                      textColor="blue.500"
                      textDecoration="underline"
                      onClick={(e) => openInvestmentScreeningDetailInNewTab(e, item.projectId || "", item.targetReviewDate)}
                      onAuxClick={(e) => openInvestmentScreeningDetailInNewTab(e, item.projectId || "", item.targetReviewDate)}
                    >
                      <TruncatedCell label={item.projectName || ''}>{item.projectName}</TruncatedCell>
                    </Link>
                  </Td>
                  {/* 1: 振り返り日 */}
                  <Td borderRight="1px solid" borderColor="gray.200">{item.targetReviewDate}</Td>
                  {/* 2: フェーズ */}
                  <Td borderRight="1px solid" borderColor="gray.200">
                    {(() => {
                      const phaseLabel = item.reviewPhase
                        ? ReviewPhasePresets.find(p => p.value === item.reviewPhase)?.label ?? item.reviewPhase
                        : '';
                      return <TruncatedCell label={phaseLabel}>{phaseLabel}</TruncatedCell>;
                    })()}
                  </Td>
                  {/* 3: 状態 */}
                  <Td borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap">{renderStatusBadge(item)}</Td>
                  {/* 4: 目的 */}
                  <Td borderRight="1px solid" borderColor="gray.200">{getPurposeTypeNameByCode(item.purpose)}</Td>
                  {/* 5: 設備用途区分 */}
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={getEquipmentUsageTypeNameByCode(item.equipmentUsageType || '') || ''}>
                      {getEquipmentUsageTypeNameByCode(item.equipmentUsageType || '')}
                    </TruncatedCell>
                  </Td>
                  {/* 6: 事業本部 */}
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={getHeadquartersNameFromHierarchy(item)}>
                      {getHeadquartersNameFromHierarchy(item)}
                    </TruncatedCell>
                  </Td>
                  {/* 7: 事業部 */}
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={getDivisionNameFromHierarchy(item)}>
                      {getDivisionNameFromHierarchy(item)}
                    </TruncatedCell>
                  </Td>
                  {/* 8: 起案者 */}
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={item.registrantName || ''}>{item.registrantName}</TruncatedCell>
                  </Td>
                  {/* 9: 申請番号 */}
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={item.applicationNumber || ''}>{item.applicationNumber}</TruncatedCell>
                  </Td>
                  {/* 10: 案件名 */}
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <TruncatedCell label={item.opportunityName || ''}>{item.opportunityName}</TruncatedCell>
                  </Td>
                  {/* 11: MMI製品名 */}
                  <Td>
                    <TruncatedCell label={item.mmiProductName || ''}>{item.mmiProductName}</TruncatedCell>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default ReviewSearchTab;
